import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import { AlertCircle, BookOpen, RefreshCw } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { getCurrentUser } from "../../../utils/auth";
import SyllabusProgressHeader from "./SyllabusProgressHeader";
import StudentSyllabusDayCard from "./StudentSyllabusDayCard";
import "./studentSyllabus.scss";

/**
 * StudentSyllabus
 *
 * Read-only syllabus timeline for students.
 * Supports two access patterns:
 *   1. /s/batch/:batchId/syllabus  — batchId from URL
 *   2. /s/syllabus                 — auto-resolve via student profile
 */
const StudentSyllabus = ({ batchId: propBatchId }) => {
  const { batchId: urlBatchId } = useParams();

  const [batchId, setBatchId] = useState(propBatchId || urlBatchId || null);
  const [batchData, setBatchData] = useState(null);
  const [syllabusData, setSyllabusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Track which cards are expanded — auto-expand current day
  const [expandedDays, setExpandedDays] = useState(new Set());

  // ── Resolve batchId if not in URL or props ──
  useEffect(() => {
    if (propBatchId) {
      setBatchId(propBatchId);
      return;
    }
    if (urlBatchId) {
      setBatchId(urlBatchId);
      return;
    }
    // Fetch student profile to get batch
    const resolveFromStudent = async () => {
      try {
        const user = getCurrentUser();
        const studentId = user?.studentId;
        if (!studentId) {
          setError("Could not determine your batch. Please contact support.");
          setLoading(false);
          return;
        }
        const { data } = await apiClient.get(`/students/${studentId}`);
        const batch = data.student?.batch;
        if (batch?._id) {
          setBatchId(batch._id);
        } else {
          setError("You are not enrolled in any batch yet.");
          setLoading(false);
        }
      } catch {
        setError("Failed to load your profile.");
        setLoading(false);
      }
    };
    resolveFromStudent();
  }, [propBatchId, urlBatchId]);

  // ── Fetch syllabus ──
  const fetchSyllabus = useCallback(
    async () => {
      if (!batchId) return;
      setLoading(true);
      setError("");
      try {
        // Fetch batch data
        const batchRes = await apiClient.get(`/batches/${batchId}`);
        setBatchData(batchRes.data.batch || batchRes.data);

        // Fetch syllabus
        const { data } = await apiClient.get(
          `/syllabus/batch/${batchId}/syllabus`
        );
        setSyllabusData(data);

        // Auto-expand current day (first non-completed)
        if (data.sessions?.length) {
          const currentDay = data.sessions.find(
            (s) => s.status !== "completed"
          );
          if (currentDay) {
            setExpandedDays(new Set([currentDay.dayNumber]));
          }
        }
      } catch (err) {
        const status = err?.response?.status;
        const msg = err?.response?.data?.message || "";
        if (status === 404) {
          setSyllabusData(null);
          setError(
            msg.includes("Batch")
              ? "Batch not found."
              : "Syllabus has not been initialized for this batch yet."
          );
        } else {
          setSyllabusData(null);
          setError(msg || "Failed to load syllabus.");
        }
      } finally {
        setLoading(false);
      }
    },
    [batchId]
  );

  useEffect(() => {
    if (batchId) fetchSyllabus();
  }, [batchId, fetchSyllabus]);

  // ── Determine current day ──
  const currentDayNumber =
    syllabusData?.sessions?.find((s) => s.status !== "completed")
      ?.dayNumber ?? null;

  // ── Toggle expand/collapse ──
  const toggleExpand = (dayNumber) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayNumber)) next.delete(dayNumber);
      else next.add(dayNumber);
      return next;
    });
  };

  // ── Loading state ──
  if (loading) {
    return (
      <div className="student-syllabus-page">
        <div className="ss-loading">
          <Spinner animation="border" size="sm" />
          <span>Loading syllabus…</span>
        </div>
      </div>
    );
  }

  // ── Error state (with retry) ──
  if (error && !syllabusData) {
    return (
      <div className="student-syllabus-page">
        <div className="ss-header-top">
          <h1>Course Syllabus</h1>
          <p>Track your learning progress</p>
        </div>

        {/* Special empty state for "not initialized" */}
        {error.includes("not been initialized") ? (
          <Alert variant="info" className="ss-error-alert">
            <AlertCircle size={18} className="me-2" />
            {error}
          </Alert>
        ) : (
          <Alert variant="danger" className="ss-error-alert">
            <AlertCircle size={18} className="me-2" />
            {error}
            <button
              className="btn btn-outline-danger btn-sm ss-retry-btn"
              onClick={fetchSyllabus}
            >
              <RefreshCw size={14} className="me-1" />
              Retry
            </button>
          </Alert>
        )}
      </div>
    );
  }

  // ── Empty state (no sessions) ──
  if (
    !syllabusData ||
    !syllabusData.sessions ||
    syllabusData.sessions.length === 0
  ) {
    return (
      <div className="student-syllabus-page">
        <div className="ss-header-top">
          <h1>Course Syllabus</h1>
          <p>Track your learning progress</p>
        </div>
        <div className="ss-empty-state">
          <div className="ss-empty-icon">
            <BookOpen size={32} />
          </div>
          <h3>Syllabus has not been initialized for this batch yet.</h3>
          <p>
            Your instructor will set up the syllabus timeline once the batch
            begins. Check back later!
          </p>
        </div>
      </div>
    );
  }

  // ── Main render ──
  return (
    <div className="student-syllabus-page">
      {/* Progress header */}
      <SyllabusProgressHeader
        totalDays={syllabusData.totalDays}
        completedDays={syllabusData.completedDays}
        progressPercent={syllabusData.progressPercent}
      />

      {/* Day cards timeline */}
      <div className="ss-timeline">
        {syllabusData.sessions.map((session) => (
          <StudentSyllabusDayCard
            key={session.dayNumber}
            session={session}
            batchId={batchId}
            isCurrent={session.dayNumber === currentDayNumber}
            isExpanded={expandedDays.has(session.dayNumber)}
            onToggleExpand={() => toggleExpand(session.dayNumber)}
            batchTiming={{
              startTime: batchData?.startTime,
              endTime: batchData?.endTime,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default StudentSyllabus;
